import queryString from 'qs';
import { acquireToken } from '@alixpartners/ui-utils';
import { BlockBlobClient } from '@azure/storage-blob';
import { AbortSignalLike } from '@azure/abort-controller';
import merge from 'lodash/merge';
import {
  IUploadedFile,
  IReport,
  TFileSection,
  TFileStatus,
  SECTIONS_MAP,
  FILE_STATUSES_MAP,
} from '../types';

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;
const REACT_APP_AUTH_RESOURCE_ID = process.env.REACT_APP_AUTH_RESOURCE_ID;
const REACT_APP_FXR_CLIENT_ID = process.env.REACT_APP_FXR_CLIENT_ID;
const REACT_APP_FXR_API_URL = process.env.REACT_APP_FXR_API_URL;

const addAuthHeaders = (token: string, options = { headers: {} }) => ({
  ...options,
  headers: {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  },
});

const parseTextResponse = (textResponse: string) => {
  try {
    const jsonResponse = JSON.parse(textResponse);
    return jsonResponse;
  } catch {
    return textResponse;
  }
};

export class TokenError extends Error {
  errorType: string = 'tokenError';
  constructor(message: string) {
    super(message);
    this.message = message;
    Error.captureStackTrace(this, TokenError);
  }
}

class ApiError extends Error {
  statusCode: number;
  constructor(statusCode: number, message: string) {
    super(message);
    this.statusCode = statusCode;
    this.message = message;
    Error.captureStackTrace(this, ApiError);
  }
}

const jsonRequest = async (
  url: string,
  options = {},
  withHeaders = false
): Promise<any> => {
  const defaultOptions = {
    headers: { 'content-type': 'application/json' },
  };

  if (url.indexOf('/') === 0) {
    url = `${REACT_APP_API_URL}${url}`;
  }

  try {
    const response = await fetch(url, merge(defaultOptions, options));
    const textResponse = await response.text();
    const operationLocation = response.headers.get('operation-location');
    let parserdResponse = {} as any;

    if (textResponse === '' && operationLocation) {
      parserdResponse.operationLocation = operationLocation;
    } else {
      parserdResponse = parseTextResponse(textResponse);
    }

    if (!response.ok) {
      throw new ApiError(
        response.status,
        parserdResponse.hasOwnProperty('errorMessage')
          ? parserdResponse.errorMessage
          : JSON.parse(textResponse)
      );
    }

    if (
      !parserdResponse &&
      (response.status === 204 || response.status === 202)
    ) {
      return {
        status: response.status,
      };
    }

    return withHeaders
      ? {
          body: parserdResponse,
          headers: response.headers,
        }
      : parserdResponse;
  } catch (e) {
    const error = e as any;

    if (error.statusCode === 403) {
      window.history.replaceState(
        {
          state: {
            aadUrl: error.message?.aadUrl,
            aadName: error.message?.aadName,
          },
        },
        '',
        '/accessdenied'
      );
      window.location.replace('/accessdenied');
    } else {
      throw new ApiError(error.statusCode, error.message);
    }
  }
};

const requestWithToken = async (
  url: string,
  defaults: any,
  options: any,
  withHeader = false,
  resourceId: string = REACT_APP_AUTH_RESOURCE_ID || ''
) => {
  try {
    const token = await acquireToken(false, resourceId);
    if (!token) {
      throw new ApiError(401, 'ServerError (401)');
    }
    const authDefaults = addAuthHeaders(token, defaults);
    return await jsonRequest(
      url,
      {
        ...authDefaults,
        ...options,
      },
      withHeader
    );
  } catch (error: any) {
    console.group('API Error:');
    console.log(error ? error.error : error);
    console.log(error);
    console.groupEnd();
    if (error?.errorType && error.errorType === 'interaction_required') {
      throw new TokenError(error.message);
    }
  }
};

export const getRequest = (url: string, options = {}) => {
  const defaults = { method: 'GET' };

  return requestWithToken(url, defaults, options);
};

export const postRequest = (url: string, options = {}) => {
  const defaults = { method: 'POST' };
  return requestWithToken(url, defaults, options);
};

export const putRequest = (url: string, options = {}) => {
  const defaults = { method: 'PUT' };
  return requestWithToken(url, defaults, options);
};

export const putRequestWithHeaders = (url: string, options = {}) => {
  const defaults = { method: 'PUT' };
  return requestWithToken(url, defaults, options, true);
};

export const patchRequest = (url: string, options = {}) => {
  const defaults = { method: 'PATCH' };
  return requestWithToken(url, defaults, options);
};

export const deleteRequest = (url: string, options = {}) => {
  const defaults = { method: 'DELETE' };
  return requestWithToken(url, defaults, options);
};

export const fetchExpenseDetail = async () => {
  return await getRequest('/v1/expenses/getDetailedExpenses');
};

export const fetchSettings = async () => {
  return await getRequest('/v1/settings');
};

export const patchSettings = async (data: { [key: string]: any }) => {
  const options = {
    body: JSON.stringify(data),
  };

  return await patchRequest('/v1/settings', options);
};

export const fetchConfiguration = () => {
  const url = '/v1/configuration';

  return getRequest(url);
};

export const fetchPowerBiPBIDSFile = () => {
  const url = '/reports/v1/powerBI/pbids';

  return getRequest(url);
};

export const patchPowerBiReport = (reports: Array<Partial<IReport>>) => {
  const url = '/reports/v1/powerBI';
  const options = {
    body: JSON.stringify({ reports }),
  };
  return patchRequest(url, options);
};

export const fetchPowerBiSharedReports = () => {
  const url = '/reports/v1/powerBI/shared';
  return getRequest(url);
};

export const fetchPowerBiPublishedReports = fetchPowerBiSharedReports;

export const fetchPowerBiReports = () => {
  const url = '/reports/v1/powerBI';
  return getRequest(url);
};

export const createSharedPowerBiReport = (reportId: string) => {
  const url = `/reports/v1/powerBI/shared/${reportId}`;

  return postRequest(url);
};

export const removeSharedPowerBiReport = (reportId: string) => {
  const url = `/reports/v1/powerBI/shared/${reportId}`;

  return deleteRequest(url);
};

export const removeSession = (sessionId: string) => {
  const url = `/v1/files/session/${sessionId}`;

  return deleteRequest(url);
};

export const removeSessionFromFxr = (sessionId: string) => {
  const url = `/files/${sessionId}`;

  return deleteRequestToFxr(url);
};

export const removeFile = (fileId: string) => {
  const url = `/v1/files/${fileId}`;

  return deleteRequest(url);
};

export const deleteAllData = () => {
  const url = '/v1/files/deleteall';

  return deleteRequest(url);
};

export const fetchPermissions = () => {
  const url = '/myPermissions';

  return getRequest(url);
};

export const getPowerBiEmbedData = (reportId: string, groupId: string) => {
  const params = queryString.stringify({ groupId });
  const url = `/reports/v1/powerBI/${reportId}/embedDetails?${params}`;

  return postRequest(url);
};

export const fetchFiles = (section: TFileSection): Promise<IUploadedFile[]> => {
  const url = `/v1/files/${SECTIONS_MAP[section]}`;

  return getRequest(url);
};

export const uploadFilesToCp = (
  fileData: Array<Omit<IUploadedFile, 'id' | 'uploadDate'>>
): Promise<number> => {
  const url = '/v1/files';
  const options = {
    body: JSON.stringify(
      fileData.map((f) => ({
        ...f,
        section: SECTIONS_MAP[f.section],
        status: FILE_STATUSES_MAP[f.status],
      }))
    ),
  };

  return postRequest(url, options);
};

export const updateSessionState = (
  sessionId: string,
  params: { status: TFileStatus; fileUrls?: string[]; rawFileUrls?: string[] }
): Promise<number> => {
  const url = `/v1/files/${sessionId}?isSecure=true`;

  const options = {
    body: JSON.stringify(params),
  };

  return putRequest(url, options);
};

export const deleteRequestToFxr = (url: string, options = {}) => {
  const defaults = { method: 'DELETE' };
  url = `${REACT_APP_FXR_API_URL}${url}`;

  return requestWithToken(
    url,
    defaults,
    options,
    false,
    REACT_APP_FXR_CLIENT_ID
  );
};

export const getRequestToFxr = (url: string, options = {}) => {
  const defaults = { method: 'GET' };
  url = `${REACT_APP_FXR_API_URL}${url}`;

  return requestWithToken(
    url,
    defaults,
    options,
    false,
    REACT_APP_FXR_CLIENT_ID
  );
};

export const postRequestToFxr = (url: string, options = {}) => {
  const defaults = { method: 'POST' };
  url = `${REACT_APP_FXR_API_URL}${url}`;

  return requestWithToken(
    url,
    defaults,
    options,
    false,
    REACT_APP_FXR_CLIENT_ID
  );
};

export const patchRequestToFxr = (url: string, options = {}) => {
  const defaults = { method: 'PATCH' };
  url = `${REACT_APP_FXR_API_URL}${url}`;

  return requestWithToken(
    url,
    defaults,
    options,
    false,
    REACT_APP_FXR_CLIENT_ID
  );
};

export const triggerEtlPublish = () => {
  return postRequest('/v1/etl/trigger/wct');
};

export const fetchEtlStatus = () => {
  return getRequest('/v1/etl/status');
};

export const getProjectCodeName = (): Promise<string> => {
  return getRequest('/v1/configuration/projectCodeName');
};

export const createFileUploadSession = (
  schema: string,
  tag: string | null
): Promise<{
  sessionId: string;
  uploadUrl: string;
}> => {
  const options: { [key: string]: any } = {};
  options.body = `{ "targetSchema": ${schema}, "tag": "${tag}" }`;
  return postRequestToFxr('/files', options);
};

export const uploadFileToFxr = (
  url: string,
  file: File,
  abortSignal: AbortSignalLike
): Promise<{ fileName: string }> => {
  const { origin, pathname, search } = new URL(url);

  const blobClient = new BlockBlobClient(
    `${origin}${pathname}/${file.name}${search}`
  );

  return new Promise((resolve, reject) => {
    blobClient
      .uploadData(file, { abortSignal })
      .then(() =>
        resolve({
          fileName: file.name,
        })
      )
      .catch((error) => {
        reject(error);
      });
  });
};

export const getSessionStatuses = (
  sessionIdList: string[]
): Promise<
  Array<{
    sessionId: string;
    status: string;
    malformedRowsUrl?: string;
    expirationDate?: string;
  }>
> => {
  return getRequestToFxr(
    `/Files/sessions/status?sessionIds=${sessionIdList.join('&sessionIds=')}`
  );
};

interface ISessionJobData {
  sessionId: string;
  originalReturnUrl: string;
  state: number;
  stateDescription: string;
  fileUrls: string[];
  rawFileUrls: string[];
}

export const getFileUrls = (sessionId: string): Promise<ISessionJobData> => {
  return getRequestToFxr(`/Files/${sessionId}/job?isSecure=true`);
};

export const getSchema = (section: TFileSection): Promise<any> => {
  return getRequest(`/v1/files/schema/${SECTIONS_MAP[section]}`);
};

export const deleteSessions = async (sessionIdList: string[]): Promise<any> => {
  return await Promise.all(
    sessionIdList.map((sessionId) => removeSessionFromFxr(sessionId))
  );
  // TODO: use single request to delete multiple sessions
  // return deleteRequestToFxr(
  //   `/Files/sessions?sessionIds=${sessionIdList.join('&sessionIds=')}`
  // );
};
